/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    Icon,
    Image,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React, {forwardRef, useContext} from 'react'
import {IntlContext, defineMessage} from 'react-intl'

// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.
import '@salesforce/retail-react-app/app/assets/svg/alert.svg'
import '@salesforce/retail-react-app/app/assets/svg/check-circle.svg'
import '@salesforce/retail-react-app/app/assets/svg/check.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-down.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-left.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-right.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-up.svg'
import '@salesforce/retail-react-app/app/assets/svg/dashboard.svg'
import '@salesforce/retail-react-app/app/assets/svg/figma-logo.svg'
import '@salesforce/retail-react-app/app/assets/svg/file.svg'
import '@salesforce/retail-react-app/app/assets/svg/filter.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-ca.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-cn.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-fr.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-gb.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-it.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-jp.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-us.svg'
import '@salesforce/retail-react-app/app/assets/svg/github-logo.svg'
import '@salesforce/retail-react-app/app/assets/svg/heart-solid.svg'
import '@salesforce/retail-react-app/app/assets/svg/heart.svg'
import '@salesforce/retail-react-app/app/assets/svg/info.svg'
import '@salesforce/retail-react-app/app/assets/svg/like.svg'
import '@salesforce/retail-react-app/app/assets/svg/lock.svg'
import '@salesforce/retail-react-app/app/assets/svg/plug.svg'
import '@salesforce/retail-react-app/app/assets/svg/signout.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-facebook.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-instagram.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-twitter.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-youtube.svg'
import '@salesforce/retail-react-app/app/assets/svg/user.svg'
import '@salesforce/retail-react-app/app/assets/svg/visibility-off.svg'
import '@salesforce/retail-react-app/app/assets/svg/visibility.svg'
import '../../assets/svg/accessibility.svg'
import '../../assets/svg/account-fr.svg'
import '../../assets/svg/account.svg'
import '../../assets/svg/address.svg'
import '../../assets/svg/basket.svg'
import '../../assets/svg/booking-tick.svg'
import '../../assets/svg/bucket.svg'
import '../../assets/svg/caret-circle-left.svg'
import '../../assets/svg/caret-circle-right.svg'
import '../../assets/svg/check-circle-green.svg'
import '../../assets/svg/chevron-down-gray.svg'
import '../../assets/svg/chevron-down.svg'
import '../../assets/svg/chevron-right.svg'
import '../../assets/svg/chevron-up-gray.svg'
import '../../assets/svg/close.svg'
import '../../assets/svg/empty-cart.svg'
import '../../assets/svg/facebook.svg'
import '../../assets/svg/fragrance.svg'
import '../../assets/svg/haircare.svg'
import '../../assets/svg/hamburger.svg'
import '../../assets/svg/heart-filled.svg'
import '../../assets/svg/heart-outline.svg'
import '../../assets/svg/instagram.svg'
import '../../assets/svg/left-chevron.svg'
import '../../assets/svg/link-break.svg'
import '../../assets/svg/linkedin.svg'
import '../../assets/svg/logout.svg'
import '../../assets/svg/long-arrow-banner.svg'
import '../../assets/svg/long-arrow-category.svg'
import '../../assets/svg/long-arrow.svg'
import '../../assets/svg/long-next-arrow.svg'
import '../../assets/svg/long-prev-arrow.svg'
import '../../assets/svg/makeup.svg'
import '../../assets/svg/mega-nav-right-arrow.svg'
import '../../assets/svg/menu.svg'
import '../../assets/svg/minus.svg'
import '../../assets/svg/next-arrow.svg'
import '../../assets/svg/no-result.svg'
import '../../assets/svg/pause.svg'
import '../../assets/svg/play.svg'
import '../../assets/svg/plus-circle-booking.svg'
import '../../assets/svg/plus-circle.svg'
import '../../assets/svg/plus.svg'
import '../../assets/svg/previous-arrow.svg'
import '../../assets/svg/profile.svg'
import '../../assets/svg/receipt.svg'
import '../../assets/svg/right-arrow.svg'
import '../../assets/svg/right-chevron.svg'
import '../../assets/svg/search.svg'
import '../../assets/svg/skincare.svg'
import '../../assets/svg/sound-off.svg'
import '../../assets/svg/sound-on.svg'
import '../../assets/svg/tiktok.svg'
import '../../assets/svg/trash.svg'
import '../../assets/svg/twitter.svg'
import '../../assets/svg/warning.svg'
import '../../assets/svg/x-circle-red.svg'
import '../../assets/svg/alert-triangle.svg'
import '../../assets/svg/calendar-minus.svg'
import '../../assets/svg/user.svg'

// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
import AmexSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-amex.svg'
import CVVSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-cvv.svg'
import DiscoverSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-discover.svg'
import MastercardSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-mastercard.svg'
import VisaSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-visa.svg'
import PaypalSymbol from '@salesforce/retail-react-app/app/assets/svg/paypal.svg'
import SocialPinterestSymbol from '@salesforce/retail-react-app/app/assets/svg/social-pinterest.svg'
import BrandLogoSymbol from '../../assets/svg/brand-logo.svg'
import LocationSymbol from '../../assets/svg/location.svg'

export {RoundDotIcon} from './round-dot-icon'

import '../../assets/svg/accordion-collapse.svg'
import '../../assets/svg/accordion-open.svg'
import {classNames} from '../../utils/classNames'

// TODO: We're hardcoding the `viewBox` for these imported SVGs temporarily as the
// SVG loader plugin is not properly providing us the symbol data on the client side.
AmexSymbol.viewBox = AmexSymbol.viewBox || '0 0 38 22'
BrandLogoSymbol.viewBox = '0 0 117 26'
CVVSymbol.viewBox = CVVSymbol.viewBox || '0 0 41 24'
DiscoverSymbol.viewBox = DiscoverSymbol.viewBox || '0 0 38 22'
LocationSymbol.viewBox = LocationSymbol.viewBox || '0 0 16 21'
MastercardSymbol.viewBox = MastercardSymbol.viewBox || '0 0 38 22'
PaypalSymbol.viewBox = PaypalSymbol.viewBox || '0 0 80 20'
SocialPinterestSymbol.viewBox = SocialPinterestSymbol.viewBox || '0 0 21 20'
VisaSymbol.viewBox = VisaSymbol.viewBox || '0 0 38 22'

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 * @param {Object} passProps - props that will be passed onto the underlying Icon component
 * @param {Object} localizationAttributes - attributes with localized values that will be passed
 *      onto the underlying Icon component, use `defineMessage` to create localized string.
 *      Additionally, if the icon is rendered outside the provider tree, you'll also need to
 *      pass an intl object from react-intl as a prop to translate the messages.
 */
/* istanbul ignore next */
export const icon = (name, passProps, localizationAttributes) => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '')
    const component = forwardRef((props, ref) => {
        const theme = useTheme()
        // NOTE: We want to avoid `useIntl` here because that throws when <IntlProvider> is not in
        // the component ancestry, but we only enforce `intl` if we have `localizationAttributes`.
        let intl = useContext(IntlContext)
        if (localizationAttributes) {
            if (props?.intl) {
                const {intl: intlProp, ...otherProps} = props
                // Allow `props.intl` to take precedence over the intl we found
                intl = intlProp
                props = otherProps
            }
            if (!intl) {
                throw new Error(
                    'To localize messages, you must either have <IntlProvider> in the component ancestry or provide `intl` as a prop'
                )
            }
            Object.keys(localizationAttributes).forEach((key) => {
                passProps[key] = intl.formatMessage(localizationAttributes[key])
            })
        }
        const baseStyle = theme?.components?.Icon?.baseStyle
        return (
            <Icon
                ref={ref}
                role="img"
                aria-label={name}
                {...baseStyle}
                {...props}
                {...passProps}
            >
                <use role="presentation" xlinkHref={`#${name}`} />
            </Icon>
        )
    })

    component.propTypes = {
        intl: PropTypes.object
    }

    component.displayName = `${displayName}Icon`
    return component
}

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const AmexIcon = icon('cc-amex', {viewBox: AmexSymbol.viewBox})
export const AlertIcon = icon('alert')
export const AccountIcon = icon('account', {viewBox: '0 0 68 34'})
export const AccountFrIcon = icon('account-fr', {viewBox: '0 0 68 34'})
export const ProfileIcon = icon('profile')
export const BrandLogo = icon('brand-logo', {viewBox: BrandLogoSymbol.viewBox})
export const BasketIcon = icon('basket')
export const CheckIcon = icon('check')
export const CheckCircleIcon = icon('check-circle')
export const ChevronDownIcon = icon('chevron-down')
export const ChevronDownGrayIcon = icon('chevron-down-gray', {
    viewBox: '0 0 20 20'
})
export const ChevronUpGrayIcon = icon('chevron-up-gray', {
    viewBox: '0 0 20 20'
})
export const LongNextArrow = icon('long-next-arrow')
export const LongPrevArrow = icon('long-prev-arrow')
export const ChevronLeftIcon = icon('chevron-left')
export const ChevronRightIcon = icon('chevron-right')
export const ChevronUpIcon = icon('chevron-up')
export const CVVIcon = icon('cc-cvv', {viewBox: CVVSymbol.viewBox})
export const DashboardIcon = icon('dashboard')
export const DiscoverIcon = icon('cc-discover', {
    viewBox: DiscoverSymbol.viewBox
})
export const FigmaLogo = icon('figma-logo')
export const FilterIcon = icon('filter')
export const FileIcon = icon('file')
export const FlagCAIcon = icon('flag-ca')
export const FlagUSIcon = icon('flag-us')
export const FlagGBIcon = icon('flag-gb')
export const FlagFRIcon = icon('flag-fr')
export const FlagITIcon = icon('flag-it')
export const FlagCNIcon = icon('flag-cn')
export const FlagJPIcon = icon('flag-jp')
export const GithubLogo = icon('github-logo')
export const HamburgerIcon = icon('hamburger')
export const InfoIcon = icon('info')
export const LikeIcon = icon('like')
export const LockIcon = icon(
    'lock',
    {
        'aria-hidden': false,
        focusable: true
    },
    {
        'aria-label': defineMessage({
            id: 'icons.assistive_msg.lock',
            defaultMessage: 'Secure'
        })
    }
)
export const LocationIcon = icon('location')
export const PaypalIcon = icon('paypal', {viewBox: PaypalSymbol.viewBox})
export const PlugIcon = icon('plug')
export const PlusIcon = icon('plus')
export const MinusIcon = icon('minus')
export const MastercardIcon = icon('cc-mastercard', {
    viewBox: MastercardSymbol.viewBox
})
export const ReceiptIcon = icon('receipt')
export const SearchIcon = icon('search')
export const SocialFacebookIcon = icon('social-facebook')
export const SocialInstagramIcon = icon('social-instagram')
export const SocialPinterestIcon = icon('social-pinterest', {
    viewBox: SocialPinterestSymbol.viewBox
})
export const SocialTwitterIcon = icon('social-twitter')
export const SocialYoutubeIcon = icon('social-youtube')
export const SkincareIcon = icon('skincare')
export const MakeupIcon = icon('makeup')
export const FragranceIcon = icon('fragrance')
export const HairCareIcon = icon('haircare')
export const SignoutIcon = icon('signout')
export const UserIcon = icon('user')
export const VisaIcon = icon('cc-visa', {viewBox: VisaSymbol.viewBox})
export const VisibilityIcon = icon('visibility')
export const VisibilityOffIcon = icon('visibility-off')
export const HeartIcon = icon('heart')
export const HeartSolidIcon = icon('heart-solid')
export const CloseIcon = icon('close', {viewBox: '0 0 20 20'})
export const LogOutIcon = icon('logout', {viewBox: '0 0 71 34'})
export const LeftChevronIcon = icon('left-chevron', {viewBox: '0 0 9 16'})
export const RightChevronIcon = icon('right-chevron', {viewBox: '0 0 9 16'})
export const MegaNavRightArrowIcon = icon('mega-nav-right-arrow')
export const InstagramIcon = icon('instagram')
export const FacebookIcon = icon('facebook')
export const TwitterIcon = icon('twitter')
export const TiktokIcon = icon('tiktok')
export const LinkedInIcon = icon('linkedin')
export const RightArrowIcon = icon('right-arrow', {viewBox: '0 0 20 20'})
export const RemoveIcon = icon('trash')

export const SoundOnIcon = icon('sound-on')
export const SoundOffIcon = icon('sound-off')
export const LongArrowIcon = icon('long-arrow')
export const LongArrowCategoryIcon = icon('long-arrow-category')
export const LongArrowBannerIcon = icon('long-arrow-banner')

export const HeartOutlinedIcon = icon('heart-outline')
export const ChevronSelectDownIcon = icon('chevron-down')
export const HeartFilledIcon = icon('heart-filled')
export const AddressIcon = icon('address')
export const MenuIcon = icon('menu')
export const EmptyCartIcon = icon('empty-cart')
export const NoResultIcon = icon('no-result')
export const LinkBreakIcon = icon('link-break')

export const AccordionCollapsedIcon = icon('accordion-collapse')
export const AccordionOpenIcon = icon('accordion-open')
export const WarningIcon = icon('warning')

export const XCircleRedIcon = icon('x-circle-red')
export const PlusCircleIcon = icon('plus-circle')
export const PlusCircleBookingIcon = icon('plus-circle-booking')
export const CheckCircleGreenIcon = icon('check-circle-green')
export const AccessibilityIcon = icon('accessibility')
export const BucketIcon = icon('bucket')
export const CaretCircleLeftIcon = icon('caret-circle-left')
export const CaretCircleRightIcon = icon('caret-circle-right')

export const PlayIcon = icon('play')
export const PauseIcon = icon('pause')
export const BookingTicketIcon = icon('booking-tick', {viewBox: '0 0 56 56'})

export const NextArrowIcon = icon('next-arrow', {viewBox: '0 0 29 16'})
export const PreviousArrowIcon = icon('previous-arrow', {viewBox: '0 0 29 16'})
export const AlertTriangleIcon = icon('alert-triangle')
export const CalendarMinus = icon('calendar-minus')

const iconPropTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    className: PropTypes.string,
    styles: PropTypes.objectOf(PropTypes.string)
}

export const LogoSmallBlackIcon = ({
    width = 155,
    height = 55,
    className = '',
    styles
}) => (
    <Image
        src={getAssetUrl('static/svgs/Logo-Small--black.svg')}
        width={width}
        height={height}
        alt="Logo Black"
        className={classNames('icon__logo-small--black', className)}
        {...(styles && {style: styles})}
    />
)

LogoSmallBlackIcon.propTypes = iconPropTypes

export const LogoSmallWhiteIcon = ({
    width = 155,
    height = 55,
    className = '',
    styles
}) => (
    <Image
        src={getAssetUrl('static/svgs/Logo-Small--white.svg')}
        width={width}
        height={height}
        alt="Logo White"
        className={classNames('icon__logo-small--white', className)}
        {...(styles && {style: styles})}
    />
)

LogoSmallWhiteIcon.propTypes = iconPropTypes

export const LogoBlackIcon = ({
    width = 664,
    height = 150,
    className = '',
    styles
}) => (
    <Image
        src={getAssetUrl('static/svgs/Logo--black.svg')}
        width={width}
        height={height}
        alt="Logo Black"
        className={classNames('icon__logo--black', className)}
        {...(styles && {style: styles})}
    />
)

LogoBlackIcon.propTypes = iconPropTypes
